import { KycAnalyzeIllustration, KycAnalyzeFailedIllustration } from '../../../../../../assets/illustrations';
import { getProfileDataThunk } from '../../../../../../store/workspace/thunks';
import { objectURLToFile } from '../../../../../../utils/generalHelpers';
import { uploadFileThunk } from '../../../../../../store/general/thunks';
import { useDispatch, useSelector } from '../../../../../../store/store';
import { selectUserId } from '../../../../../../store/auth/selectors';
import { usePhotoCapture } from '../../../hooks/usePhotoCapture';
import { kycThunk } from '../../../../../../store/exam/thunks';
import Modal, { ModalActions, ModalButtonProps } from '../../../../../../components/Modal';
import Spinner from '../../../../../../components/Spinner';
import { KycMismatch, KycStep } from '../../../types';
import { kycCaptureCopy } from '../../../constants';
import CaptureStep from './CaptureStep';
import styles from './style.module.scss';
import { useState } from 'react';

interface Props {
    onClose: () => void;
    /** Called once the applicant's identity has been accepted. */
    onVerified: () => void;
};

/**
 * Walks the applicant through KYC: a photo of their face, a photo of their ID,
 * then the verdict. Both captures stay alive for the whole run, so stepping back
 * shows what was already taken.
 */
function KYCWizard({ onClose, onVerified }: Props) {
    const dispatch = useDispatch();
    const userId = useSelector(selectUserId);

    const face = usePhotoCapture();
    const id = usePhotoCapture();

    const [step, setStep] = useState<KycStep>('Face');
    const [isRejected, setIsRejected] = useState(false);
    const [facePhoto, setFacePhoto] = useState<string | null>(null);
    const [mismatches, setMismatches] = useState<KycMismatch[]>([]);

    const verify = async (facePhotoURL: string, idPhotoURL: string) => {
        try {
            const faceFile = await objectURLToFile(facePhotoURL, 'face.png', 'image/png');
            const idFile = await objectURLToFile(idPhotoURL, 'id.png', 'image/png');

            const faceTag = (await dispatch(uploadFileThunk({ fileName: `${userId}/face.png`, file: faceFile }))).payload;
            const idTag = (await dispatch(uploadFileThunk({ fileName: `${userId}/id.png`, file: idFile }))).payload;

            await dispatch(kycThunk({ e_tags: [faceTag, idTag] })).unwrap();

            await dispatch(getProfileDataThunk());
            onVerified();
        } catch (error: any) {
            setIsRejected(true);
            setMismatches(error?.mismatches || []);
        }
    };

    const goToId = () => {
        const photo = face.readPhoto();
        if (!photo) return;

        setFacePhoto(photo);
        setStep('Id');
    };

    const submit = () => {
        const idPhoto = id.readPhoto();
        if (!facePhoto || !idPhoto) return;

        setStep('Result');
        verify(facePhoto, idPhoto);
    };

    const startOver = () => {
        face.clear();
        id.clear();
        setFacePhoto(null);
        setMismatches([]);
        setIsRejected(false);
        setStep('Face');
    };

    /**
     * Every step is driven from the same footer: the two captures step forward
     * and back, and the verdict either restarts the wizard or leaves it.
     */
    const stepActions: Record<KycStep, {
        actions: ModalActions;
        dismissButton?: ModalButtonProps;
        confirmButton?: ModalButtonProps;
    }> = {
        Face: {
            actions: 'both',
            dismissButton: { text: 'Cancel', type: 'Outline-Dark', onClick: onClose },
            confirmButton: { text: 'Continue', type: 'Primary', onClick: goToId, disabled: !face.hasPhoto },
        },
        Id: {
            actions: 'both',
            dismissButton: { text: 'Back', type: 'Outline-Dark', onClick: () => setStep('Face') },
            confirmButton: { text: 'Continue', type: 'Primary', onClick: submit, disabled: !id.hasPhoto },
        },
        Result: {
            // Nothing to act on while the verdict is still being worked out.
            actions: isRejected ? 'both' : 'none',
            dismissButton: { text: 'Try again', type: 'Outline-Dark', onClick: startOver },
            confirmButton: { text: 'Verify later', type: 'Primary', onClick: onClose },
        },
    };

    return (
        <Modal
            size='md'
            closeFunc={onClose}
            title='KYC Verification'
            {...stepActions[step]}
        >
            <div className={styles.wrapper}>
                {step === 'Face' && <CaptureStep
                    capture={face}
                    copy={kycCaptureCopy.Face}
                />}

                {step === 'Id' && <CaptureStep
                    capture={id}
                    copy={kycCaptureCopy.Id}
                />}

                {step === 'Result' && <div className={styles.pending}>
                    {!isRejected && <>
                        <KycAnalyzeIllustration />
                        <span className={styles.title}>
                            Analyzing KYC information
                        </span>
                        <span className={styles.text}>
                            We are analyzing your KYC information. Please wait a moment.
                        </span>
                        <Spinner />
                    </>}

                    {isRejected && <>
                        <KycAnalyzeFailedIllustration />
                        <span className={styles.title} data-is-error='true'>
                            KYC was unsuccessful!
                        </span>
                        <span className={styles.text}>
                            Please ensure the accuracy of your information.
                        </span>

                        {mismatches.length > 0 && <div className={styles.mismatches}>
                            <span className={styles.mismatches_title}>
                                Mismatch{mismatches.length > 1 ? 'es' : ''}:
                            </span>
                            <ul>
                                {mismatches.map((item, index) => <li className={styles.item} key={index}>
                                    <span className={styles.label}>{item.type}: </span>
                                    <span className={styles.value}>{item.value || 'N/A'}</span>
                                </li>)}
                            </ul>
                        </div>}
                    </>}
                </div>}
            </div>
        </Modal>
    );
};

export default KYCWizard;
